import { useEffect, useId, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Radio, RadioGroup } from '../../../components/Radio';
import { TextInput } from '../../../components/TextInput';
import { AdminInviteError, createSetupInvitation } from '../../../services/adminInvites';
import type {
  CreateSetupInvitationRequest,
  InviteSport,
  ResolveInviteResponse,
} from '../../../types/adminInvites';
import { formatAnnualPrice, sportLabel } from '../../signup/signupFormat';
import { commonMessage, parseDollarsToCents, serverMessage } from './inviteForm';
import type { InviteDone } from './inviteTypes';

const SPORTS: readonly InviteSport[] = ['baseball', 'softball'];
const MAX_PROGRAM_NAME_LENGTH = 255;

interface InviteNewProgramStepProps {
  resolved: ResolveInviteResponse;
  headingRef: RefObject<HTMLHeadingElement | null>;
  /** Set when the admin came here from a match, so they can go back to it. */
  onBack?: () => void;
  onDone: (done: InviteDone) => void;
  onBlocked: (message: string, organizationName: string | null) => void;
}

type FieldErrors = Partial<Record<'name' | 'sport' | 'price', string>>;

/**
 * The text fields in page order, so focus lands on the first one that is wrong. The sport error is
 * not focused: it is already a role="alert" and the radios have no ref to take.
 */
const FOCUSABLE_FIELDS = ['name', 'price'] as const;

/**
 * A setup invitation: the invitee creates the program and becomes its admin. The name and sport
 * are defaults, and the invitee can change both when they accept.
 */
export const InviteNewProgramStep = ({
  resolved,
  headingRef,
  onBack,
  onDone,
  onBlocked,
}: InviteNewProgramStepProps) => {
  const { newProgram, school, catalogSportsTaken } = resolved;
  const suggestedName = (s: InviteSport | null): string =>
    (s ? newProgram.suggestedNames[s] : null) ??
    newProgram.suggestedNames.baseball ??
    newProgram.suggestedNames.softball ??
    '';

  const [sport, setSport] = useState<InviteSport | null>(newProgram.suggestedSport);
  const [name, setName] = useState(() => suggestedName(newProgram.suggestedSport));
  // Once the admin types a name, picking a sport stops overwriting it.
  const [nameEdited, setNameEdited] = useState(false);
  const [requiresPayment, setRequiresPayment] = useState(false);
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const paymentHintId = useId();
  const priceHintId = useId();
  const priceInputId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const priceRef = useRef<HTMLInputElement>(null);
  // Server refusals arrive while the inputs are disabled, so focus waits until they are enabled.
  const pendingFocusRef = useRef<(typeof FOCUSABLE_FIELDS)[number] | null>(null);

  useEffect(() => {
    const field = pendingFocusRef.current;
    if (busy || !field) return;
    pendingFocusRef.current = null;
    (field === 'name' ? nameRef : priceRef).current?.focus();
  }, [busy, fieldErrors]);

  /** Shows the field errors and queues focus on the first one. */
  const showFieldErrors = (errors: FieldErrors) => {
    setFieldErrors(errors);
    pendingFocusRef.current = FOCUSABLE_FIELDS.find((field) => errors[field]) ?? null;
  };

  const catalogPrice = newProgram.catalogAnnualPriceCents;
  const sportTaken = sport !== null && catalogSportsTaken.includes(sport);

  const chooseSport = (next: InviteSport) => {
    setSport(next);
    setFieldErrors((e) => ({ ...e, sport: undefined }));
    if (!nameEdited) setName(suggestedName(next));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;

    const errors: FieldErrors = {};
    if (name.trim().length > MAX_PROGRAM_NAME_LENGTH) {
      errors.name = `Keep the name to ${MAX_PROGRAM_NAME_LENGTH} characters or fewer.`;
    }
    const parsed = requiresPayment ? parseDollarsToCents(price) : { cents: null, error: null };
    if (parsed.error) errors.price = parsed.error;
    if (requiresPayment && parsed.cents === null && !parsed.error && catalogPrice === null) {
      errors.price = 'This school has no catalog price, so enter one.';
    }
    if (Object.keys(errors).length > 0) {
      showFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const request: CreateSetupInvitationRequest = {
      email: resolved.email,
      requiresPayment,
      ...(name.trim() ? { programName: name.trim() } : {}),
      ...(sport ? { sport } : {}),
      ...(parsed.cents !== null ? { customAnnualPriceCents: parsed.cents } : {}),
    };
    const base = {
      kind: 'setup' as const,
      email: resolved.email,
      programName: request.programName ?? null,
      requiresPayment,
      priceCents: requiresPayment ? (parsed.cents ?? catalogPrice) : null,
      request,
    };

    setBusy(true);
    setError(null);

    try {
      const row = await createSetupInvitation(request);
      onDone({ ...base, sendFailed: false, setupInvitationId: row.id });
    } catch (err: unknown) {
      const failure = err instanceof AdminInviteError ? err : new AdminInviteError('failed', '');
      switch (failure.kind) {
        case 'send_failed':
          onDone({
            ...base,
            sendFailed: true,
            setupInvitationId: failure.setupInvitation?.id ?? null,
          });
          return;
        case 'blocked':
          onBlocked(failure.message, failure.organization?.name ?? null);
          return;
        case 'payment_unavailable':
          setError('Payments are turned off right now. Untick Require payment to send it free.');
          break;
        case 'invalid': {
          const message = serverMessage(failure.message) ?? 'Check the details and try again.';
          if (failure.code === 'invalid_price' || failure.code === 'price_required') {
            showFieldErrors({ price: message });
          } else if (failure.code === 'invalid_program_name') {
            showFieldErrors({ name: message });
          } else if (failure.code === 'invalid_sport') {
            showFieldErrors({ sport: message });
          } else {
            setError(message);
          }
          break;
        }
        default:
          setError(commonMessage(failure.kind, failure.message));
      }
      setBusy(false);
    }
  };

  return (
    <form className="invite-user-dialog__body" onSubmit={submit} noValidate>
      <h3 className="invite-user-dialog__step-title" ref={headingRef} tabIndex={-1}>
        New program for {resolved.email}
      </h3>
      <p className="invite-user-dialog__text">
        They get a link to create the program and become its admin. They can change the name and
        sport when they accept.
      </p>

      {resolved.pendingSetupInvitation && (
        <p className="invite-user-dialog__hint">
          A setup invitation is already open for this address. Sending again replaces it.
        </p>
      )}

      <TextInput
        label="Program name"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setNameEdited(true);
        }}
        error={fieldErrors.name}
        disabled={busy}
        ref={nameRef}
        data-testid="invite-program-name"
      />

      <div className="invite-user-dialog__field">
        <RadioGroup label="Sport" testId="invite-sports">
          {SPORTS.map((option) => (
            <Radio
              key={option}
              name="invite-sport"
              value={option}
              label={sportLabel(option)}
              checked={sport === option}
              onChange={() => chooseSport(option)}
              disabled={busy}
              data-testid={`invite-sport-${option}`}
            />
          ))}
        </RadioGroup>
        {fieldErrors.sport && (
          <p className="invite-user-dialog__field-error" role="alert">
            {fieldErrors.sport}
          </p>
        )}
        {sportTaken && (
          <p className="invite-user-dialog__notice" data-testid="invite-sport-taken">
            {school?.name ?? 'This school'} already has a {sportLabel(sport).toLowerCase()} program
            here. This one will not be linked to the school.
          </p>
        )}
      </div>

      <div className="invite-user-dialog__field">
        <Checkbox
          label="Require payment"
          checked={requiresPayment}
          onChange={(e) => {
            setRequiresPayment(e.target.checked);
            setFieldErrors((f) => ({ ...f, price: undefined }));
          }}
          disabled={busy || !newProgram.stripeAvailable}
          aria-describedby={paymentHintId}
          data-testid="invite-require-payment"
        />
        <p className="invite-user-dialog__hint" id={paymentHintId}>
          {newProgram.stripeAvailable
            ? 'They pay by card after they sign in. Leave it off for a free program.'
            : 'Payments are turned off, so this program will be free.'}
        </p>
      </div>

      {requiresPayment && (
        <div>
          <TextInput
            label="Annual price in dollars (optional)"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={catalogPrice !== null ? String(catalogPrice / 100) : ''}
            id={priceInputId}
            error={fieldErrors.price}
            ref={priceRef}
            // Passing our own describedby replaces TextInput's, so its error id is added back here.
            aria-describedby={
              fieldErrors.price ? `${priceHintId} ${priceInputId}-error` : priceHintId
            }
            disabled={busy}
            data-testid="invite-price"
          />
          <p className="invite-user-dialog__hint" id={priceHintId}>
            {catalogPrice !== null
              ? `Leave blank to use the catalog price, ${formatAnnualPrice(catalogPrice, 'usd')}.`
              : 'This school has no catalog price, so enter one.'}
          </p>
        </div>
      )}

      {error && (
        <p className="invite-user-dialog__error" role="alert">
          {error}
        </p>
      )}

      <div className="invite-user-dialog__actions">
        {onBack && (
          <Button type="button" variant="secondary" onClick={onBack} disabled={busy}>
            Back
          </Button>
        )}
        <Button type="submit" aria-disabled={busy} aria-busy={busy} data-testid="invite-send-setup">
          {busy ? 'Sending...' : 'Send setup invitation'}
        </Button>
      </div>
    </form>
  );
};
